import { Role, rolesManipulator } from 'auth';
import { MouseEvent, useCallback, useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerRejectEntityModal } from '@/modules/issuer-dashboard/components/modals/IssuerRejectEntityModal.tsx';
import { useIssuerUpdateEntityStatusMutation } from '@/modules/issuer-dashboard/queries/entities.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Button, ConfirmModal, DisclosureListItem, ModalProviderContext } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EntityStatus, EntityWithDocumentResponse } from 'api';

interface PendingHolderRequestItemProps {
  item: EntityWithDocumentResponse;
}

export const PendingHolderRequestItem = ({ item }: PendingHolderRequestItemProps) => {
  const userRole = rolesManipulator.current_user_role;
  const isPendingHolder = item.status === EntityStatus.EntityAwaitingApproval;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const { openModal, onCloseAll } = useContext(ModalProviderContext);
  const { mutateAsync: updateEntityStatus, isLoading } = useIssuerUpdateEntityStatusMutation({
    onSuccess: onCloseAll,
  });

  const openHolder = useCallback(() => {
    navigate(
      {
        pathname: issuerDashboardPaths.dynamic.holder(String(item.id)),
        search: params.toString(),
      },
      {
        state: { from: pathname + '?' + params.toString() },
      },
    );
  }, [item.id, navigate, params, pathname]);

  const handleApprove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      openModal(ConfirmModal, {
        title: 'You sure you want to approve this holder?',
        closeAfterConfirm: false,
        onConfirm: async () => {
          await updateEntityStatus({
            id: item.id,
            status: EntityStatus.EntityApproved,
            entity_application_rejection_reason: '',
          });
        },
      });
    },
    [item.id, openModal, updateEntityStatus],
  );

  const handleDecline = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      openModal(IssuerRejectEntityModal, { id: item.id });
    },
    [item.id, openModal],
  );

  return (
    <DisclosureListItem
      onClick={openHolder}
      leftMainString={
        <>
          {item.entity_name || 'Entity name is unset'}
          {!!item.type && ` (${fixNameEnum(item.type)})`}
        </>
      }
      leftSubduedContentSet={[dateToPrint(item.created_at, DateVariant.Dashboard, true)]}
      classes={{ leftContent: 'w-[200px]' }}
      rightContent={
        userRole === Role.SR_ISSUER_ADMIN && isPendingHolder ? (
          <div className="flex gap-3" onClick={event => event.stopPropagation()}>
            <Button variant="secondary" className="w-[100px]" onClick={handleDecline}>
              Decline
            </Button>
            <Button
              variant="primary"
              className="w-[100px]"
              onClick={handleApprove}
              loading={isLoading}
              disabled={isLoading}
            >
              Confirm
            </Button>
          </div>
        ) : undefined
      }
    />
  );
};
